import { memo, useEffect } from "react";
import { useSelector } from "react-redux";
import { get as _get } from "lodash";
import { utils as koilibUtils } from "koilib";
import { BigNumber } from "bignumber.js";

// components
import { Box, Button, InputBase, Skeleton, Tooltip, Typography, IconButton } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TokenLogo from "../TokenLogo";
import { amountType } from "../../theme/main";
import { useTokenBalance } from "../../hooks/queries";
import { formatAmount } from "../../utils/format";
import { useWallet } from "../../hooks/useWallet";

// utils
import { numberPattern } from "./../../utils/regex";

const pillSx = {
  flexShrink: 0,
  height: 40,
  borderRadius: 999,
  paddingInline: "6px 10px",
  gap: 1,
};

const TokenNotExist = ({ onSelect }) => {
  // selector
  const networkSelector = useSelector((state) => state.settings.network);

  return (
    <Button
      variant="contained"
      disabled={networkSelector == "UNSUPPORTED"}
      onClick={onSelect}
      endIcon={<KeyboardArrowDownIcon />}
      sx={{ ...pillSx, paddingInline: "14px 10px" }}
    >
      Select token
    </Button>
  );
};

const TokenExist = ({ token, onSelect }) => (
  <Button
    onClick={() => onSelect()}
    aria-label={`change token, ${_get(token, "symbol", "")} selected`}
    endIcon={<KeyboardArrowDownIcon sx={{ color: "text.secondary" }} />}
    sx={{
      ...pillSx,
      color: "text.primary",
      fontSize: "1rem",
      backgroundColor: "background.paper",
      "&:hover": { backgroundColor: "secondary.dark" },
    }}
  >
    <TokenLogo token={token} size={28} />
    {_get(token, "symbol", "")}
  </Button>
);

const copyTokenAddress = async (token) => {
  try {
    await navigator.clipboard.writeText(token.address);
  } catch (err) {
    console.log(err);
  }
};

// a well with an optional label ("You pay"), the balance with Max/Half, the amount and the token pill
const TokenInputPanel = ({ token, inputVal, onSelect, onBalance, onInput, disabledLoading, label = "" }) => {
  // selectors
  const walletSelector = useWallet();

  // balance, shared with every component showing the same token (hooks/queries.js)
  const owner = _get(walletSelector, "wallet[0].address", null);
  const balanceQuery = useTokenBalance(_get(token, "address", null), owner);
  const rawBalance = balanceQuery.data;
  const loadingBalance = rawBalance == null && !balanceQuery.isError;
  const balanceError = rawBalance == null && balanceQuery.isError;
  const balance =
    rawBalance != null
      ? token.decimals != "0"
        ? koilibUtils.formatUnits(rawBalance, token.decimals)
        : rawBalance
      : "loading...";

  useEffect(() => {
    if (rawBalance != null) onBalance(rawBalance);
  }, [rawBalance]);

  const hasWallet = Boolean(_get(walletSelector, "wallet", null));

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "14px",
        backgroundColor: "background.light",
        border: 1,
        borderColor: "transparent",
        transition: "border-color 120ms ease",
        "&:focus-within": { borderColor: "secondary.dark" },
      }}
    >
      {label || (token && hasWallet) ? (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: 26, mb: 1 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {label}
          </Typography>
          {token && hasWallet ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Typography variant="body2" component="span" sx={{ color: "text.secondary" }}>
                Balance{" "}
                {loadingBalance ? (
                  <Skeleton component="span" variant="text" width={56} sx={{ display: "inline-block" }} />
                ) : balanceError ? (
                  "unavailable"
                ) : (
                  formatAmount(balance, { maxDecimals: 6 })
                )}
              </Typography>
              <Button
                variant="dark-grey-contained"
                size="small"
                onClick={() => {
                  numberPattern(balance, onInput);
                }}
              >
                Max
              </Button>
              <Button
                variant="dark-grey-contained"
                size="small"
                sx={{ display: { xs: "none", sm: "inline-flex" } }}
                onClick={() => {
                  numberPattern(balance, (b) => {
                    let fullAmount = koilibUtils.parseUnits(b, token.decimals);
                    let _amount = new BigNumber(fullAmount).dividedBy(2).toFixed(0, 1);
                    let value = token.decimals != "0" ? koilibUtils.formatUnits(_amount, token.decimals) : _amount;
                    onInput(value);
                  });
                }}
              >
                Half
              </Button>
            </Box>
          ) : null}
        </Box>
      ) : null}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <InputBase
          disabled={disabledLoading}
          placeholder="0"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={inputVal}
          slotProps={{ input: { "aria-label": label ? `${label} amount` : "amount" } }}
          sx={{
            ...amountType,
            flex: 1,
            minWidth: 0,
            fontSize: { xs: "1.625rem", sm: "2rem" },
            color: "text.primary",
            "& input::placeholder": { color: "text.disabled", opacity: 1 },
            "&.Mui-disabled input": { WebkitTextFillColor: "inherit", opacity: 0.6 },
          }}
          onChange={(e) => numberPattern(e.currentTarget.value, onInput)}
        />
        {!token ? <TokenNotExist onSelect={() => onSelect()} /> : <TokenExist token={token} onSelect={onSelect} />}
        {token !== null ? (
          <Tooltip title="Copy token address">
            <IconButton
              aria-label="copy token address"
              size="small"
              onClick={() => {
                copyTokenAddress(token);
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
    </Box>
  );
};

export default memo(TokenInputPanel);
